import { useRef, useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { AppLoader } from '@src/components/AppLoader/AppLoader'
import { newSessionMutation } from './InHouseGame'
import { fetchQuery } from 'relay-runtime'
import {
  Currency,
  InHouseGame2CreateInHouseSessionMutation,
} from './__generated__/InHouseGame2CreateInHouseSessionMutation.graphql'
import {
  GameGetNormalizedGameDataQuery,
  GameGetNormalizedGameDataQuery$data,
} from '../Game/__generated__/GameGetNormalizedGameDataQuery.graphql'
import { getNormalizedGameData } from '../Game/Game.queries'
import { commitMutation } from 'relay-runtime'
import Env from '../../RelayEnvironment'
import { store } from '@src/store'
import styled from 'styled-components'
import { Deck } from '@blackjackfun/ui-components'
import { ThumbnailSlider } from '../ThumbnailSlider'
import { useSimpleStrapiFetch } from '../Strapi/Strapi.hook'
import StrapiSEO from '../Strapi/Strapi.SEO'
import { components } from '../Strapi/Strapi.api.types'

const ResponsiveIframe = styled.iframe`
  width: 100%;
  border-radius: 0.375rem;
  max-width: 1280px;
  height: 37rem;

  @media (max-width: 1100px) {
    height: calc(100dvh - 150px);
  }
`

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding-bottom: 70px;
  gap: 15px;
`

const Container = styled.div`
  padding: 0 10px;
  width: 100%;
  max-width: 1280px;

  @media (min-width: 768px) {
    padding: 0;
  }
`

const ClassicIHG = ({ gameId }: { gameId: string }): JSX.Element => {
  const history = useHistory()
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [sourceDetails, setSourceDetails] = useState<any>(null)
  const walletStore = store.getState().wallet
  const [activeWallet, setActiveWallet] = useState(walletStore?.activeWallet)
  const [gameMetadata, setGameMetadata] = useState<GameGetNormalizedGameDataQuery$data['getNormalizedGame'] | null>(
    null
  )

  const { data, loading, error } = useSimpleStrapiFetch<components['schemas']['GamePageListResponse']>({
    collection: 'game-pages',
    populate: ['SEO'],
    filters: { gameId: `${gameId}99` },
  })

  const gamePage = !loading && !error && data && data.data && data.data.length > 0 ? data.data[0] : null
  const content = gamePage ? gamePage.Content : null
  const seo = gamePage ? gamePage.SEO : null

  const providerHandleIHG = 'bjfun'

  const fetchGameMetadata = () => {
    fetchQuery<GameGetNormalizedGameDataQuery>(
      Env,
      getNormalizedGameData,
      {
        gameCode: `${providerHandleIHG}:${gameId}`,
      },
      { fetchPolicy: 'network-only' }
    ).subscribe({
      next: (data) => {
        setGameMetadata(data.getNormalizedGame)
      },
    })
  }

  const updateSource = (activeWallet) => {
    commitMutation<InHouseGame2CreateInHouseSessionMutation>(Env, {
      mutation: newSessionMutation,
      variables: {
        input: {
          gameCode: `${providerHandleIHG}:${gameId}`,
          currency: activeWallet.currency as Currency,
          virtualId: activeWallet.virtualId,
        },
      },
      onCompleted: ({ createInHouseSession }) => {
        if (createInHouseSession) {
          setSourceDetails(createInHouseSession)
        }
      },
      onError: (err: any) => {
        console.error(err)
      },
    })
  }

  // Reset state when gameId changes
  useEffect(() => {
    setSourceDetails(null)
    setGameMetadata(null)
    if (activeWallet) {
      updateSource(activeWallet)
      fetchGameMetadata()
    }
  }, [gameId])

  useEffect(() => {
    if (activeWallet?.currency !== walletStore.activeWallet?.currency) {
      setActiveWallet(walletStore.activeWallet)
      updateSource(walletStore.activeWallet)
    }
  }, [walletStore.activeWallet?.currency])

  useEffect(() => {
    if (activeWallet && !sourceDetails) {
      updateSource(activeWallet)
      fetchGameMetadata()
    }
  }, [activeWallet])

  return (
    <ContentContainer>
      <StrapiSEO seo={seo} />
      {gameMetadata && sourceDetails && sourceDetails.parameters ? (
        <>
          <ResponsiveIframe
            key={`${providerHandleIHG}-${gameId}-${sourceDetails.sessionId}`} // Add gameId and sessionId to key
            ref={iframeRef}
            title={gameId}
            src={`${sourceDetails.parameters.host}?session_id=${sourceDetails.sessionId}`}
            allowFullScreen
          />
          <Container>
            <Deck
              label={gameMetadata.label}
              provider={gameMetadata.meta.arbitrary_metadata.provider}
              rtp={gameMetadata.rtp || 99}
              tags={gameMetadata.meta.arbitrary_metadata.tags}
              provablyFair={gameMetadata.meta.arbitrary_metadata.provably_fair}
              maxWin={gameMetadata.meta.arbitrary_metadata.max_win}
              releaseDate={gameMetadata.releaseDate || ''}
              history={history}
              description={content}
            />
          </Container>
          <Container>
            <ThumbnailSlider category="similar" gameCode={`bjfun:${gameId}`} autoPlay={false} />
          </Container>
        </>
      ) : (
        <AppLoader />
      )}
    </ContentContainer>
  )
}

export default ClassicIHG
